import {ff, i18n, Icon} from 'mc/lib';
import SubjektApi from 'mc/lib/api/gen/subjekty/SubjektApi';
import {ActionsRow} from 'mc/lib/detail';
import {Form, FormActions, FormBody} from 'mc/lib/form';
import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import {toastr} from 'react-redux-toastr';
import {Alert, Button, Card, CardBlock, CardHeader} from 'reactstrap';
import {FormSection, formValueSelector, propTypes, reduxForm} from 'redux-form';

export default class SubjektForm extends PureComponent {

    static propTypes = {
        subjektClass: PropTypes.string.isRequired,
        subjektFields: PropTypes.func.isRequired,
        verifyEnabled: PropTypes.bool,
        ...propTypes,
    };

    static defaultProps = {
        verifyEnabled: false,
    };

    constructor(props) {
        super(props);

        const {initialValues} = props;

        this.state = {
            verifying: false,
            odpovednaOsobaVisible: initialValues && initialValues.odpovednaOsoba != null,
        };
    }

    handleAddOdpovednaOsoba = () => {
        this.setState({odpovednaOsobaVisible: true});
    };

    handleRemoveOdpovednaOsoba = () => {
        this.setState({odpovednaOsobaVisible: false});
    };

    handleVerify = () => {
        const {subjektClass, subjekt, change} = this.props;
        const {id, adresaDoruc, telefon, ...otherValues} = subjekt;
        const valuesToVerify = addSubjektClassProperties(otherValues, subjektClass);

        this.setState({verifying: true});

        SubjektApi.overSubjekt(valuesToVerify, window.SUBJEKT_KOD_ROLE)
            .then(results => {
                this.setState({verifying: false});

                if (results.length > 1) {
                    toastr.warn(i18n('subjektForm.verifyResult.many'));
                } else if (results.length === 1) {
                    const newSubjekt = results[0].subjekt;
                    change('subjekt', {...newSubjekt, id, adresaDoruc, telefon});
                    toastr.success(i18n('subjektForm.verifyResult.success'));
                } else if (results.length === 0) {
                    toastr.warn(i18n('subjektForm.verifyResult.none'));
                }
            })
            .catch(error => {
                console.error(error);
                this.setState({verifying: false});
            });
    };

    interceptSubmit = values => {
        const {onSubmit, subjektClass} = this.props;
        const {odpovednaOsobaVisible} = this.state;
        const {odpovednaOsoba} = values;

        if (odpovednaOsobaVisible && odpovednaOsoba == null) {
            values = {...values, odpovednaOsoba: {}};
        } else if (!odpovednaOsobaVisible && odpovednaOsoba != null) {
            values = {...values, odpovednaOsoba: null};
        }

        values = addAllClassProperties(values, subjektClass);

        return onSubmit(values);
    };

    isDisabled() {
        return this.state.verifying || this.props.submitting;
    }

    renderVerifyButton() {
        return (
            <Button disabled={this.isDisabled()} onClick={this.handleVerify}>
                {this.state.verifying && <Icon icon="refresh" animation="spin"/>}
                {' ' + i18n("subjekt.form.action.overit")}
            </Button>
        )
    }

    renderSubjektCard() {
        return (
            <Card>
                <CardHeader>
                    <ActionsRow actions={this.renderSubjektActions()}>
                        <h5>{i18n('subjektForm.udajeSubjektu')}</h5>
                    </ActionsRow>
                </CardHeader>
                <CardBlock>
                    {this.props.subjektFields({disabled: this.isDisabled()})}
                </CardBlock>
            </Card>
        );
    }

    renderSubjektActions() {
        return this.props.verifyEnabled ? this.renderVerifyButton() : null;
    }

    renderOdpovednaOsobaCard() {
        const disabled = this.isDisabled();
        return (
            <Card>
                <CardHeader>
                    <ActionsRow actions={this.renderOdpovednaOsobaRemoveButton()}>
                        <h5>{i18n('subjektForm.odpovednaOsoba')}</h5>
                    </ActionsRow>
                </CardHeader>
                <CardBlock>
                    <FormSection name="odpovednaOsoba">
                        <ff.String name="jmeno" label="subjekt.form.title.jmeno" disabled={disabled}/>
                        <ff.String name="prijmeni" label="subjekt.form.title.prijmeni" disabled={disabled}/>
                        <ff.Kontakt name="telefon" label="subjekt.form.title.kontaktVychTelefon" kontaktType="TEL" disabled={disabled}/>
                        <ff.Kontakt name="email" label="subjekt.form.title.kontaktVychEmail" kontaktType="EMAIL" disabled={disabled}/>
                    </FormSection>
                </CardBlock>
            </Card>
        );
    }

    renderOdpovednaOsobaAddButton() {
        return (
            <Button className="mt-2" color="link" onClick={this.handleAddOdpovednaOsoba} disabled={this.isDisabled()}>
                <Icon icon="plus"/> {i18n('subjektForm.odpovednaOsoba.add')}
            </Button>
        );
    }

    renderOdpovednaOsobaRemoveButton() {
        return (
            <Button onClick={this.handleRemoveOdpovednaOsoba} disabled={this.isDisabled()}>
                {i18n('global.remove')}
            </Button>
        );
    }

    render() {
        const {handleSubmit, onClose, error} = this.props;
        const {odpovednaOsobaVisible} = this.state;
        const disabled = this.isDisabled();

        return (
            <Form horizontal onSubmit={handleSubmit(this.interceptSubmit)}>
                <FormBody cards>
                    {error && <Alert color="danger">{error}</Alert>}
                    {this.renderSubjektCard()}
                    {odpovednaOsobaVisible
                        ? this.renderOdpovednaOsobaCard()
                        : this.renderOdpovednaOsobaAddButton()}
                </FormBody>
                <FormActions okButton cancelButton onClose={onClose} disabled={disabled}/>
            </Form>
        );
    }

}

function addAllClassProperties(values, subjektClass) {
    if (values == null) {
        return values;
    }
    const {subjekt = {}, odpovednaOsoba, ...otherValues} = values;
    return {
        subjekt: addSubjektClassProperties(subjekt, subjektClass),
        odpovednaOsoba: odpovednaOsoba ? addFyzickaOsobaClassProperty(odpovednaOsoba) : odpovednaOsoba,
        ...otherValues,
    };
}

function addSubjektClassProperties(subjekt, subjektClass) {
    const {fyzickaOsoba} = subjekt;
    return {
        ...subjekt,
        fyzickaOsoba: fyzickaOsoba ? addFyzickaOsobaClassProperty(fyzickaOsoba) : fyzickaOsoba,
        '@class': subjektClass,
    };
}

function addFyzickaOsobaClassProperty(fyzickaOsoba) {
    return {
        ...fyzickaOsoba,
        '@class': 'FyzickaOsobaVO',
    };
}

export function connectSubjektForm(component, formConfig) {
    const selectFormValue = formValueSelector(formConfig.form);

    const mapStateToProps = state => ({
        subjekt: selectFormValue(state, 'subjekt'),
    });

    const connectState = connect(mapStateToProps);
    const connectForm = reduxForm(formConfig);

    return connectForm(connectState(component));
}
